import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRandomWord, isValidWord } from './wordList';
import './WordleGame.css';

const MAX_GUESSES = 6;
const WORD_LENGTH = 5;

function checkGuess(guess, answer) {
  const result = Array(WORD_LENGTH).fill('absent');
  const answerArr = answer.split('');
  const guessArr = guess.split('');

  // First pass: correct positions
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guessArr[i] === answerArr[i]) {
      result[i] = 'correct';
      answerArr[i] = null;
      guessArr[i] = null;
    }
  }
  // Second pass: present but wrong position
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guessArr[i] === null) continue;
    const idx = answerArr.indexOf(guessArr[i]);
    if (idx !== -1) {
      result[i] = 'present';
      answerArr[idx] = null;
    }
  }
  return result;
}

function WordleGame() {
  const navigate = useNavigate();
  const [answer, setAnswer] = useState(getRandomWord);
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [shake, setShake] = useState(false);
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState(() => {
    try { return JSON.parse(localStorage.getItem('demohub_wordle_stats') || '{"played":0,"won":0,"streak":0,"best":0}'); }
    catch { return { played: 0, won: 0, streak: 0, best: 0 }; }
  });

  const keyboardStatus = {};
  guesses.forEach((g) => {
    const result = checkGuess(g, answer);
    g.split('').forEach((letter, i) => {
      const status = result[i];
      const current = keyboardStatus[letter];
      if (status === 'correct') keyboardStatus[letter] = 'correct';
      else if (status === 'present' && current !== 'correct') keyboardStatus[letter] = 'present';
      else if (!current) keyboardStatus[letter] = 'absent';
    });
  });

  const showMessage = (msg, duration = 1500) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), duration);
  };

  const submitGuess = useCallback(() => {
    if (currentGuess.length !== WORD_LENGTH) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      showMessage('Not enough letters');
      return;
    }

    if (!isValidWord(currentGuess)) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      showMessage('Not in word list');
      return;
    }

    const newGuesses = [...guesses, currentGuess];
    setGuesses(newGuesses);
    setCurrentGuess('');

    if (currentGuess === answer) {
      setWon(true);
      setGameOver(true);
      const newStats = { ...stats, played: stats.played + 1, won: stats.won + 1, streak: stats.streak + 1, best: Math.max(stats.best, stats.streak + 1) };
      setStats(newStats);
      try { localStorage.setItem('demohub_wordle_stats', JSON.stringify(newStats)); } catch {}
      showMessage(['Genius!', 'Magnificent!', 'Impressive!', 'Splendid!', 'Great!', 'Phew!'][newGuesses.length - 1], 3000);
    } else if (newGuesses.length >= MAX_GUESSES) {
      setGameOver(true);
      const newStats = { ...stats, played: stats.played + 1, streak: 0 };
      setStats(newStats);
      try { localStorage.setItem('demohub_wordle_stats', JSON.stringify(newStats)); } catch {}
      showMessage(answer, 5000);
    }
  }, [currentGuess, guesses, answer, stats]);

  const handleKey = useCallback((key) => {
    if (gameOver) return;
    if (key === 'ENTER') { submitGuess(); return; }
    if (key === 'BACKSPACE') { setCurrentGuess((g) => g.slice(0, -1)); return; }
    if (/^[A-Z]$/.test(key) && currentGuess.length < WORD_LENGTH) {
      setCurrentGuess((g) => g + key);
    }
  }, [currentGuess, gameOver, submitGuess]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const key = e.key === 'Enter' ? 'ENTER' : e.key === 'Backspace' ? 'BACKSPACE' : e.key.toUpperCase();
      handleKey(key);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleKey]);

  const handleNewGame = () => {
    setAnswer(getRandomWord());
    setGuesses([]);
    setCurrentGuess('');
    setGameOver(false);
    setWon(false);
    setMessage('');
  };

  const KEYBOARD = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACKSPACE'],
  ];

  return (
    <div className="wordle-app">
      <div className="wordle-topbar">
        <div className="wordle-topbar-left">
          <button className="wordle-back-btn" onClick={() => navigate('/demo/games')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="wordle-title">Wordle</h1>
        </div>
        <div className="wordle-topbar-right">
          <div className="wordle-stats-bar">
            <span>{stats.played} played</span>
            <span>{stats.played > 0 ? Math.round(stats.won / stats.played * 100) : 0}% win</span>
            <span>🔥 {stats.streak}</span>
          </div>
          <button className="wordle-action-btn" onClick={handleNewGame}>New Word</button>
        </div>
      </div>

      <div className="wordle-container">
        {message && <div className="wordle-message">{message}</div>}

        {/* Board */}
        <div className="wordle-board">
          {Array.from({ length: MAX_GUESSES }).map((_, rowIdx) => {
            const guess = guesses[rowIdx];
            const isCurrent = rowIdx === guesses.length && !gameOver;
            const result = guess ? checkGuess(guess, answer) : null;

            return (
              <div key={rowIdx} className={`wordle-row ${isCurrent && shake ? 'shake' : ''}`}>
                {Array.from({ length: WORD_LENGTH }).map((_, colIdx) => {
                  let letter = '';
                  let status = '';

                  if (guess) {
                    letter = guess[colIdx];
                    status = result[colIdx];
                  } else if (isCurrent) {
                    letter = currentGuess[colIdx] || '';
                  }

                  return (
                    <div
                      key={colIdx}
                      className={`wordle-cell ${status} ${letter && !guess ? 'active' : ''}`}
                      style={guess ? { animationDelay: `${colIdx * 0.15}s` } : {}}
                    >
                      {letter}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Keyboard */}
        <div className="wordle-keyboard">
          {KEYBOARD.map((row, i) => (
            <div key={i} className="wordle-kb-row">
              {row.map((key) => (
                <button
                  key={key}
                  className={`wordle-key ${keyboardStatus[key] || ''} ${key.length > 1 ? 'wide' : ''}`}
                  onClick={() => handleKey(key)}
                >
                  {key === 'BACKSPACE' ? '⌫' : key}
                </button>
              ))}
            </div>
          ))}
        </div>

        {gameOver && (
          <div className="wordle-result">
            <span>{won ? '🎉 You got it!' : `The word was ${answer}`}</span>
            <button className="wordle-play-again" onClick={handleNewGame}>Play Again</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default WordleGame;
